import { IonContent, IonHeader, IonItem, IonLabel, IonNote, IonPage, IonSpinner, IonTitle, IonToolbar, useIonAlert } from "@ionic/react";
import { IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle } from "@ionic/react";

import axios from "axios";

import { useEffect, useState } from "react";
import { IonToggleWithSpinner } from "../components/IonToggleWithSpinner";
import { UserConfig } from "../user.interface";

import { askPermission, subscribeUserToPush } from "../serviceWorkerRegistration";
import { showErrorAlert } from "../utils/showErrorAlert";

function useAxios<R>(url: string) {
  console.log("useAxios called");
  const [data, setData] = useState<R>();
  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get<R>(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

const Config: React.FC = () => {
  const { data, loading, error } = useAxios<UserConfig>("/users/me/config");
  const [config, setConfig] = useState(data);

  useEffect(() => setConfig(data), [data]);

  const ionAlert = useIonAlert();

  const handleConfigChange = async (key: string, value: boolean) => {
    if (config) {
      const updatedConfig: UserConfig = { ...config, [key]: value };
      setConfig(updatedConfig);

      if (updatedConfig.pushNotificationsEnabled) {
        try {
          const permission = await askPermission();
          if (permission) {
            const pushSubscription = await subscribeUserToPush();
            updatedConfig.pushSubscription = pushSubscription?.toJSON();
          } else {
            showErrorAlert(ionAlert, "**Surprise pickachu face**");
          }
        } catch (e) {
          showErrorAlert(ionAlert, "Notifications are not supported on your device");
          console.error("Error subscribing to push notifications", e);

          updatedConfig.pushNotificationsEnabled = false;
          updatedConfig.pushSubscription = null;
        }
      } else {
        updatedConfig.pushSubscription = null;
      }

      const savedConfig = await axios.put<UserConfig>("/users/me/config", updatedConfig);
      setConfig(savedConfig.data);
    }
  };

  if (loading) return <div className="center" children={<IonSpinner />}></div>;
  if (error) return <div className="center" children={<IonNote color="danger">! Failed to load</IonNote>}></div>;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Better VULMS</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <main style={{ height: "100%", display: "grid", placeItems: "center" }}>
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Config</IonCardTitle>
              <IonCardSubtitle>Select your notification preferences</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <div style={{ textAlign: "center" }}>
                <IonItem shape="round">
                  <IonLabel>Push Notifications</IonLabel>
                  <IonToggleWithSpinner
                    checked={config?.pushNotificationsEnabled}
                    onChange={(checked) => handleConfigChange("pushNotificationsEnabled", checked)}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel>SMS Alerts:</IonLabel>
                  <IonToggleWithSpinner
                    disabled
                    checked={config?.smsAlertsEnabled}
                    onChange={(checked) => handleConfigChange("smsAlertsEnabled", checked)}
                  />
                </IonItem>
                <IonNote style={{ "text-align": "center", "font-size": "0.5rem" }}>
                  ( SMS Alerts are only available on the premium plan )
                </IonNote>
                <IonItem>
                  <IonLabel>Email Alerts:</IonLabel>
                  <IonToggleWithSpinner
                    disabled
                    checked={config?.emailAlertsEnabled}
                    onChange={(checked) => handleConfigChange("emailAlertsEnabled", checked)}
                  />
                </IonItem>
                <IonNote style={{ "text-align": "center", "font-size": "0.5rem" }}>( And Email Alerts are not implemented yet )</IonNote>
              </div>
            </IonCardContent>
          </IonCard>
        </main>
      </IonContent>
    </IonPage>
  );
};

export default Config;
